import { 
  collection, 
  doc, 
  getDocs, 
  setDoc, 
  updateDoc, 
  deleteDoc, 
  writeBatch,
  onSnapshot,
  query,
  orderBy
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { Brand, Model, Car, DatabaseShape, Fund, Supplier } from '../types';

// Utility function to remove undefined values from objects
const removeUndefinedValues = (obj: any): any => {
  if (obj === null || obj === undefined) {
    return {};
  }
  
  // Handle arrays
  if (Array.isArray(obj)) {
    return obj.map(item => {
      if (typeof item === 'object' && item !== null && !(item instanceof Date)) {
        return removeUndefinedValues(item);
      }
      return item;
    }).filter(item => item !== undefined);
  }
  
  const cleaned: any = {};
  
  for (const [key, value] of Object.entries(obj)) {
    if (value !== undefined) {
      if (Array.isArray(value)) {
        // Clean arrays recursively
        const cleanedArray = removeUndefinedValues(value);
        if (cleanedArray.length > 0) {
          cleaned[key] = cleanedArray;
        }
      } else if (value !== null && typeof value === 'object' && !(value instanceof Date)) {
        // Recursively clean nested objects (but not Dates)
        const cleanedNested = removeUndefinedValues(value);
        if (Object.keys(cleanedNested).length > 0) {
          cleaned[key] = cleanedNested;
        }
      } else {
        // Keep all non-undefined values (including null, dates, primitives)
        cleaned[key] = value;
      }
    }
    // Skip undefined values completely
  }
  
  return cleaned;
};


// Emergency function to delete problematic car
export const deleteProblematicCar = async (carId: string): Promise<void> => {
  try {
    await deleteDoc(doc(db, COLLECTIONS.CARS, carId));
  } catch (error) {
    console.error('Failed to delete problematic car:', error);
    throw error;
  }
};

// Make it available globally for console access
(window as any).deleteProblematicCar = deleteProblematicCar;

// Collection names
const COLLECTIONS = {
  BRANDS: 'brands',
  MODELS: 'models',
  SUPPLIERS: 'suppliers',
  CARS: 'cars',
  FUND: 'fund',
  SETTINGS: 'settings'
} as const;

// Initialize database with seed data if empty
export const initializeDatabase = async (seedData: DatabaseShape): Promise<void> => {
  try {
    const batch = writeBatch(db);
    
    // Check if data already exists
    const brandsSnapshot = await getDocs(collection(db, COLLECTIONS.BRANDS));
    if (!brandsSnapshot.empty) {
      return;
    }

    // Add brands
    seedData.brands.forEach((brand) => {
      const brandRef = doc(db, COLLECTIONS.BRANDS, brand.id);
      const cleanedBrand = removeUndefinedValues(brand);
      batch.set(brandRef, cleanedBrand);
    });

    // Add models
    seedData.models.forEach((model) => {
      const modelRef = doc(db, COLLECTIONS.MODELS, model.id);
      const cleanedModel = removeUndefinedValues(model);
      batch.set(modelRef, cleanedModel);
    });

    // Add suppliers
    seedData.suppliers.forEach((supplier) => {
      const supplierRef = doc(db, COLLECTIONS.SUPPLIERS, supplier.id);
      const cleanedSupplier = removeUndefinedValues(supplier);
      batch.set(supplierRef, cleanedSupplier);
    });

    // Add cars
    seedData.cars.forEach((car) => {
      const carRef = doc(db, COLLECTIONS.CARS, car.id);
      const cleanedCar = removeUndefinedValues(car);
      batch.set(carRef, cleanedCar);
    });

    // Add fund if provided
    if (seedData.fund) {
      const fundRef = doc(db, COLLECTIONS.FUND, seedData.fund.id);
      const cleanedFund = removeUndefinedValues(seedData.fund);
      batch.set(fundRef, cleanedFund);
    }

    await batch.commit();
  } catch (error) {
    console.error('Error initializing database:', error);
    throw error;
  }
};

// Read all data from Firestore
export const readDatabase = async (): Promise<DatabaseShape> => {
  try {
    const [brandsSnapshot, modelsSnapshot, suppliersSnapshot, carsSnapshot, fundSnapshot] = await Promise.all([
      getDocs(query(collection(db, COLLECTIONS.BRANDS), orderBy('name'))),
      getDocs(query(collection(db, COLLECTIONS.MODELS), orderBy('name'))),
      getDocs(query(collection(db, COLLECTIONS.SUPPLIERS), orderBy('name'))),
      getDocs(query(collection(db, COLLECTIONS.CARS), orderBy('id'))),
      getDocs(collection(db, COLLECTIONS.FUND))
    ]);

    const brands: Brand[] = brandsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Brand));
    const models: Model[] = modelsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Model));
    const suppliers: Supplier[] = suppliersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Supplier));
    const cars: Car[] = carsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Car));
    const fund: Fund | undefined = fundSnapshot.empty ? undefined : { id: fundSnapshot.docs[0].id, ...fundSnapshot.docs[0].data() } as Fund;

    return { brands, models, suppliers, cars, fund };
  } catch (error) {
    console.error('Error reading database:', error);
    throw error;
  }
};

// Brand operations
export const addBrand = async (brand: Brand): Promise<void> => {
  try {
    const cleanedBrand = removeUndefinedValues(brand);
    await setDoc(doc(db, COLLECTIONS.BRANDS, brand.id), cleanedBrand);
  } catch (error) {
    console.error('Error adding brand:', error);
    throw error;
  }
};

export const updateBrand = async (brandId: string, updates: Partial<Brand>): Promise<void> => {
  try {
    const cleanedUpdates = removeUndefinedValues(updates);
    await updateDoc(doc(db, COLLECTIONS.BRANDS, brandId), cleanedUpdates);
  } catch (error) {
    console.error('Error updating brand:', error);
    throw error;
  }
};

export const deleteBrand = async (brandId: string): Promise<void> => {
  try {
    await deleteDoc(doc(db, COLLECTIONS.BRANDS, brandId));
  } catch (error) {
    console.error('Error deleting brand:', error);
    throw error;
  }
};

// Model operations
export const addModel = async (model: Model): Promise<void> => {
  try {
    const cleanedModel = removeUndefinedValues(model);
    await setDoc(doc(db, COLLECTIONS.MODELS, model.id), cleanedModel);
  } catch (error) {
    console.error('Error adding model:', error);
    throw error;
  }
};

export const updateModel = async (modelId: string, updates: Partial<Model>): Promise<void> => {
  try {
    const cleanedUpdates = removeUndefinedValues(updates);
    await updateDoc(doc(db, COLLECTIONS.MODELS, modelId), cleanedUpdates);
  } catch (error) {
    console.error('Error updating model:', error);
    throw error;
  }
};

export const deleteModel = async (modelId: string): Promise<void> => {
  try {
    await deleteDoc(doc(db, COLLECTIONS.MODELS, modelId));
  } catch (error) {
    console.error('Error deleting model:', error);
    throw error;
  }
};

// Car operations
export const addCar = async (car: Car): Promise<void> => {
  try {
    const cleanedCar = removeUndefinedValues(car);
    await setDoc(doc(db, COLLECTIONS.CARS, car.id), cleanedCar);
  } catch (error) {
    console.error('Error adding car:', error);
    throw error;
  }
};

export const updateCar = async (carId: string, updates: Partial<Car>): Promise<void> => {
  // EMERGENCY FIX: Manually filter out ALL undefined values immediately (including nested)
  const emergencyCleanedUpdates: any = {};
  for (const [key, value] of Object.entries(updates)) {
    if (value !== undefined) {
      if (Array.isArray(value)) {
        // Clean arrays deeply
        const cleanedArray = JSON.parse(JSON.stringify(value, (k, v) => v === undefined ? null : v));
        emergencyCleanedUpdates[key] = cleanedArray;
      } else if (value !== null && typeof value === 'object' && !(value instanceof Date)) {
        // Clean objects deeply using JSON stringify/parse trick
        const cleanedObject = JSON.parse(JSON.stringify(value, (k, v) => v === undefined ? null : v));
        emergencyCleanedUpdates[key] = cleanedObject;
      } else {
        emergencyCleanedUpdates[key] = value;
      }
    }
  }
  
  if (Object.keys(emergencyCleanedUpdates).length === 0) {
    return;
  }
  
  try {
    await updateDoc(doc(db, COLLECTIONS.CARS, carId), emergencyCleanedUpdates);
    return;
  } catch (error) {
    // Emergency cleaning failed, try detailed approach
  }
  
  // If emergency cleaning fails, try the detailed approach
  try {
    
    const cleanedUpdates = removeUndefinedValues(updates);
    
    
    // Double-check for undefined values
    const stillHasUndefined1 = Object.values(cleanedUpdates).some(value => value === undefined);
    const undefinedKeys = Object.entries(cleanedUpdates).filter(([key, value]) => value === undefined).map(([key]) => key);
    
    
    if (stillHasUndefined1 || undefinedKeys.length > 0) {
      
      // Ultra-safe manual cleaning
      const ultraCleanedUpdates: any = {};
      for (const [key, value] of Object.entries(updates)) {
        if (value !== undefined && value !== null) {
          ultraCleanedUpdates[key] = value;
        } else if (value === null) {
          // Keep null values as they are valid in Firestore
          ultraCleanedUpdates[key] = value;
        }
        // Skip undefined values completely
      }
      
      
      if (Object.keys(ultraCleanedUpdates).length === 0) {
        return;
      }
      
      await updateDoc(doc(db, COLLECTIONS.CARS, carId), ultraCleanedUpdates);
      return;
    }
    
    if (Object.keys(cleanedUpdates).length === 0) {
      return;
    }
    
    await updateDoc(doc(db, COLLECTIONS.CARS, carId), cleanedUpdates);
  } catch (error) {
    console.error('Error updating car:', error);
    throw error;
  }
};

export const deleteCar = async (carId: string): Promise<void> => {
  try {
    await deleteDoc(doc(db, COLLECTIONS.CARS, carId));
  } catch (error) {
    console.error('Error deleting car:', error);
    throw error;
  }
};

// Real-time listeners
export const subscribeToDatabase = (callback: (data: DatabaseShape) => void) => {
  const unsubscribeBrands = onSnapshot(
    query(collection(db, COLLECTIONS.BRANDS), orderBy('name')),
    () => {
      // Fetch all data when any collection changes
      readDatabase().then(callback).catch(console.error);
    }
  );

  const unsubscribeModels = onSnapshot(
    query(collection(db, COLLECTIONS.MODELS), orderBy('name')),
    () => {
      readDatabase().then(callback).catch(console.error);
    }
  );

  const unsubscribeSuppliers = onSnapshot(
    query(collection(db, COLLECTIONS.SUPPLIERS), orderBy('name')),
    () => {
      readDatabase().then(callback).catch(console.error);
    }
  );

  const unsubscribeCars = onSnapshot(
    query(collection(db, COLLECTIONS.CARS), orderBy('id')),
    () => {
      readDatabase().then(callback).catch(console.error);
    }
  );

  // Return unsubscribe function
  return () => {
    unsubscribeBrands();
    unsubscribeModels();
    unsubscribeSuppliers();
    unsubscribeCars();
  };
};

// Fund Management Functions
export const updateFund = async (fund: Fund): Promise<void> => {
  try {
    await setDoc(doc(db, COLLECTIONS.FUND, fund.id), fund);
  } catch (error) {
    console.error('Error updating fund:', error);
    throw error;
  }
};

export const getFund = async (): Promise<Fund | null> => {
  try {
    const fundSnapshot = await getDocs(collection(db, COLLECTIONS.FUND));
    if (fundSnapshot.empty) {
      return null;
    }
    
    const fundDoc = fundSnapshot.docs[0];
    return { id: fundDoc.id, ...fundDoc.data() } as Fund;
  } catch (error) {
    console.error('Error getting fund:', error);
    throw error;
  }
};

// Supplier operations
export const addSupplier = async (supplier: Supplier): Promise<void> => {
  try {
    const cleanedSupplier = removeUndefinedValues(supplier);
    await setDoc(doc(db, COLLECTIONS.SUPPLIERS, supplier.id), cleanedSupplier);
  } catch (error) {
    console.error('Error adding supplier:', error);
    throw error;
  }
};

export const updateSupplier = async (supplierId: string, updates: Partial<Supplier>): Promise<void> => {
  try {
    const cleanedUpdates = removeUndefinedValues(updates);
    await updateDoc(doc(db, COLLECTIONS.SUPPLIERS, supplierId), cleanedUpdates);
  } catch (error) {
    console.error('Error updating supplier:', error);
    throw error;
  }
};

export const deleteSupplier = async (supplierId: string): Promise<void> => {
  try {
    await deleteDoc(doc(db, COLLECTIONS.SUPPLIERS, supplierId));
  } catch (error) {
    console.error('Error deleting supplier:', error);
    throw error;
  }
};
